import { Dispatch, SetStateAction, useEffect, useRef } from 'react'

type Args = {
  showLabelListIndex: number | null
  setShowLabelListIndex: Dispatch<SetStateAction<number | null>>
}

export const useLabelPopup = (args: Args) => {
  const { showLabelListIndex, setShowLabelListIndex } = args
  const rowWrapRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const isPopupMouseDownRef = useRef(false)

  const labelInputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  const setRowRef = (rowIndex: number) => {
    return (el: HTMLDivElement | null) => {
      rowWrapRefs.current[rowIndex] = el
    }
  }

  const setLabelInputRef = (rowIndex: number) => {
    return (el: HTMLInputElement | null) => {
      labelInputRefs.current[rowIndex] = el
    }
  }

  const close = () => setShowLabelListIndex(null)
  const open = (rowIndex: number) => setShowLabelListIndex(rowIndex)

  const onPopupMouseDown = () => {
    isPopupMouseDownRef.current = true
  }

  useEffect(() => {
    const onMouseUp = () => {
      isPopupMouseDownRef.current = false
    }
    document.addEventListener('mouseup', onMouseUp)
    return () => document.removeEventListener('mouseup', onMouseUp)
  }, [])

  const onFocusLabel = (rowIndex: number) => {
    open(rowIndex)
  }

  const onBlurLabel = () => {
    if (isPopupMouseDownRef.current) return
    close()
  }

  const blurLabelInput = (rowIndex: number) => {
    const el = labelInputRefs.current[rowIndex]
    if (el) el.blur()
  }

  useEffect(() => {
    if (showLabelListIndex === null) return

    const onMouseDown = (e: MouseEvent) => {
      const idx = showLabelListIndex
      const wrap = rowWrapRefs.current[idx]
      if (!wrap) {
        setShowLabelListIndex(null)
        return
      }
      if (e.target instanceof Node && wrap.contains(e.target)) return
      setShowLabelListIndex(null)
    }

    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [showLabelListIndex, setShowLabelListIndex])

  return {
    setRowRef,
    setLabelInputRef,
    blurLabelInput,
    close,
    open,
    onPopupMouseDown,
    onFocusLabel,
    onBlurLabel,
  }
}
